/*
 * Data utilities and default content for the Jolok Banarjee portfolio.  The
 * original project was written as a set of standalone HTML and JavaScript
 * files that fetched JSON data at runtime.  This module collects the
 * default data, helper functions for merging and normalising loaded JSON,
 * and an async loader that fetches all JSON files from the public/data
 * directory.  When you build the React application the contents of
 * public/data will be included in the final bundle and available via
 * relative fetch calls (e.g. `/data/profile.json`).
 */


// Default data model - UPDATED to include thesis in education
export const defaultData = {
  profile: {
    name: "Jolok Banarjee",
    role: "Aerospace Engineering Undergraduate | AI for Aviation",
    summary:
      "Building AI-driven systems for UAVs, predictive maintenance, and disaster response. Hands-on with drones, embedded systems, and full-stack web tooling.",
    location: "Sylhet, Bangladesh",
    email: "jolok21014026@aaub.edu.bd",
    phone: "+8801781675720",
    links: {
      github: "https://github.com/jolok-banarjee",
      linkedin: "https://www.linkedin.com/in/jolok-banarjee-16825b1b7/",
      website: "https://grand-macaron-ca99d8.netlify.app/",
      facebook: "",
      instagram: ""
    },
    avatar: "assets/profile.png",
    research: [
      "Integration of Artificial Intelligence in Aviation",
      "AI‑driven predictive maintenance",
      "Deep learning and reinforcement learning for autonomous flight control",
      "Self‑adaptive and fault‑tolerant UAV navigation systems",
      "Real‑time disaster response and environmental surveillance using UAVs"
    ],
    drive_cv_link:
      "https://drive.google.com/file/d/1cUQ0xL9TifBduYIkaSI6Zl41XEw7G_9v/view?usp=sharing"
  },
  skills: {
    sections: [
      {
        title: "Programming Languages",
        items: ["Python", "C", "C++", "MATLAB", "R", "Java", "JavaScript"]
      },
      {
        title: "AI & Machine Learning",
        items: ["TensorFlow", "Keras", "scikit‑learn"]
      },
      {
        title: "CAD",
        items: ["SolidWorks", "ANSYS Mechanical", "ANSYS Fluent"]
      },
      {
        title: "Simulation & Data Analysis",
        items: ["MATLAB", "SPSS", "Tinkercad", "Proteus"]
      },
      {
        title: "Embedded Systems",
        items: ["Arduino", "Raspberry Pi", "STM32", "PWM control", "Wireless communication"]
      },
      {
        title: "Version Control & Tools",
        items: ["Git", "GitHub", "Windows", "Linux (Ubuntu)"]
      },
      {
        title: "Web & Database Technologies",
        items: ["HTML5", "CSS3", "PHP", "Bootstrap", "MySQL", "React JS", "Node", "Flask"]
      },
      {
        title: "Documentation",
        items: ["MS Word", "MS PowerPoint", "MS Excel", "Latex"]
      }
    ]
  },
  experience: {
    items: [
      {
        org: "Aviation and Aerospace University, Bangladesh (AAUB)",
        title: "Research Assistant — AI Toolkit for Predictive Maintenance of Aircraft Systems",
        start: "Feb 2026",
        end: "Present",
        location: "Dhaka, Bangladesh",
        bullets: [
          "Contribute to the funded project 'Development of an Artificial Intelligence Toolkit Based on Machine Learning for Predictive Maintenance of Aircraft Systems' under Principal Investigator Air Cdre Dr. Saifur Rahman Bakaul.",
          "Carry out data preprocessing, machine learning model development, and validation for aircraft system health monitoring and prognostics workflows.",
          "Bridge the gap between research models and deployable maintenance tools, building directly on my BSc thesis on turbofan engine fault diagnosis and remaining useful life prediction."
        ],
        logo: "assets/experience/aaub_logo.png",
        supervisor: "Air Cdre Dr. Saifur Rahman Bakaul",
        images: [],
        link: "https://www.aaub.edu.bd/",
        source: "Official Website"
      },
      {
        org: "ResearchBuddy AI — Intelligent System Engineering Lab",
        title: "Research Mentor (Research Supervisor)",
        start: "Jun 2026",
        end: "Present",
        location: "Bangladesh",
        bullets: [
          "Mentor 10–15 students at the beginning of their research journey, providing step-by-step guidance on how to start and conduct academic research.",
          "Guide students in identifying research topics, reviewing literature, formulating research questions, and planning appropriate research methods."
        ],
        logo: "",
        supervisor: "",
        images: [],
        link: "",
        source: ""
      },
      {
        org: "Frontier Alliance",
        title: "Founder & Director",
        start: "Oct 2024",
        end: "Present",
        location: "Lalmonirhat, Bangladesh",
        bullets: [
          "Lead a multidisciplinary team conducting R&D in UAV technology, disaster response and community tech education.",
          "Guide strategic planning, partnerships and outreach initiatives."
        ],
        logo: "assets/profile.png",
        supervisor: "",
        images: ["assets/project/logistics.png"],
        link: "",
        source: ""
      },
      {
        org: "Drone Club (AAUB)",
        title: "Vice President (Projects & Publications)",
        start: "Jun 2023",
        end: "Sep 2025",
        location: "Lalmonirhat, Bangladesh",
        bullets: [
          "Designed and built multiple drones including quadcopters and FPV/photo drones.",
          "Organized the Impulse 1.0 event (700+ participants) and conducted workshops on UAV development and flight control."
        ],
        logo: "assets/profile.png",
        supervisor: "",
        images: ["assets/project/voice_command.png"],
        link: "",
        source: ""
      },
      {
        org: "Adventure & Photography Club (AAUB)",
        title: "Head of IT & Communication",
        start: "Jun 2024",
        end: "Sep 2025",
        location: "Lalmonirhat, Bangladesh",
        bullets: [
          "Developed and hosted the club website using React.js on Netlify.",
          "Managed IT infrastructure and led the team in supporting events and improving the club's online presence."
        ],
        logo: "assets/profile.png",
        supervisor: "",
        images: ["assets/project/placeholder.png"],
        link: "",
        source: ""
      },
      {
        org: "AIAA Undergraduate Team Aircraft Design Competition",
        title: "Team Manager",
        start: "2024",
        end: "2025",
        location: "",
        bullets: [
          "Managed team organisation and coordination for the competition.",
          "Led aircraft configuration design, mass properties evaluation, control and stability analysis, and comparative assessments."
        ],
        logo: "assets/profile.png",
        supervisor: "",
        images: ["assets/project/sky_sentinel.png"],
        link: "",
        source: ""
      },
      {
        org: "Biman Bangladesh Airlines",
        title: "Industrial Intern",
        start: "Jun 2024",
        end: "Jul 2024",
        location: "Dhaka, Bangladesh",
        bullets: [
          "Gained hands‑on experience in airline operations and aircraft maintenance.",
          "Observed maintenance of various aircraft including Boeing 787, 777, 737 and Dash 8 fleets."
        ],
        logo: "assets/profile.png",
        supervisor: "",
        images: ["assets/project/weather_station.png"],
        link: "https://www.biman-airlines.com",
        source: "Official website"
      }
    ]
  },
  projects: {
    items: [
      {
        title: "AI-Driven Predictive Maintenance for Aircraft Engines",
        when: "Mar 2025 – Present",
        summary:
          "Engine anomaly detection, prognostics, and RUL estimation using deep learning.",
        // Mark this project as featured so it appears in the homepage featured section
        isFeatured: true,
        tags: ["Aviation", "Deep Learning", "PHM"],
        driveLink: "",
        repo: "",
        demo: "",
        details: [
          "Engine anomaly detection",
          "Engine prognostic health monitoring",
          "Remaining useful life estimation using deep learning"
        ],
        images: [
          "assets/project/placeholder.png",
          "assets/project/weather_station.png"
        ]
      },
      {
        title: "Mechanical Properties of 3D-Printed Recycled Plastics (rPET)",
        when: "May 2024 – Present",
        summary:
          "Built rPET filament machine, benchmarking against ABS/PLA/PETG on FFF prints.",
        tags: ["Sustainability", "Materials", "3D Printing"],
        driveLink: "",
        repo: "",
        demo: "",
        details: [
          "Developed a machine to produce rPET filament for 3D printing",
          "Investigated effects of printing parameters and compared rPET with ABS, PLA and PETG"
        ],
        images: [
          "assets/project/placeholder.png",
          "assets/project/voice_command.png"
        ]
      },
      {
        title: "UAS Logistics for Critical Medical & Emergency Goods",
        when: "Jul 2024 – Present",
        summary:
          "Route planning and ops concept to reach flood-prone, remote regions in Bangladesh.",
        tags: ["UAV", "Logistics", "Humanitarian"],
        driveLink: "",
        repo: "",
        demo: "",
        details: [
          "Deliver medical and emergency supplies to remote, flood-prone regions",
          "Reduce travel time for life-saving medicines and support disaster management"
        ],
        images: [
          "assets/project/logistics.png",
          "assets/project/weather_station.png"
        ]
      },
      {
        title: "Bangla Voice Command Drone",
        when: "Dec 2023 – May 2024",
        summary:
          "Speech‑NLP integrated control for robust, hands‑free drone operation in Bangla.",
        tags: ["NLP", "Embedded", "UAV"],
        driveLink: "",
        repo: "",
        demo: "",
        details: [
          "Enables users to operate drones using Bangla voice commands",
          "Integrates AI and NLP models for speech recognition",
          "Combines hardware and software for smooth drone operation in all conditions"
        ],
        images: [
          "assets/project/voice_command.png",
          "assets/project/tobacco_detection.png"
        ]
      },
      {
        title: "Tobacco Disease Identification from Real‑Time Drone Footage",
        when: "Oct 2023 – Mar 2024",
        summary:
          "Autonomous vision pipeline for crop disease detection from onboard video.",
        tags: ["CV", "Agritech", "UAV"],
        driveLink: "",
        repo: "",
        demo: "",
        details: [
          "Led development of an autonomous drone system to identify crop diseases",
          "Implemented AI‑powered computer vision algorithms for disease detection",
          "Coordinated procurement and integration of drone hardware and software"
        ],
        images: [
          "assets/project/tobacco_detection.png",
          "assets/project/logistics.png"
        ]
      },
      {
        title: "Automated Weather Satellite Ground Station",
        when: "Mar 2022 – Aug 2023",
        summary:
          "Automated ground station under PicoSat4Bangladesh designed to receive, process and visualise NOAA weather satellite data using AWS cloud services.",
        tags: ["Remote Sensing", "Satellite", "AWS"],
        image: "assets/project/weather_station.png",
        driveLink: "",
        newsLink: "",
        extra:
          "System uploads satellite signals to an S3 bucket for real‑time data visualisation and analysis.",
        repo: "",
        demo: "",
        details: [
          "Designed an automated ground station to receive, process and visualise NOAA weather satellite data using AWS cloud infrastructure",
          "Automated data collection and storage to an S3 bucket, enabling real‑time access to satellite weather data for decision making"
        ],
        images: [
          "assets/project/weather_station.png",
          "assets/project/placeholder.png"
        ]
      },
      {
        title: "Sky Sentinel S26: Next‑Generation Air‑Defense Interceptor",
        when: "Jan 2025 – Jul 2025",
        summary:
          "Final‑year design project of a long‑range interceptor designed for a dash speed of Mach 2.2 with a 750 nm combat radius, employing a low‑bypass afterburning turbofan and exhibiting exceptional climb rates and short field performance.",
        tags: ["Aerospace", "Interceptor", "Design"],
        image: "assets/project/sky_sentinel.png",
        driveLink: "",
        newsLink: "",
        extra:
          "Air‑to‑air refuelling enabled; highlights include 254 m/s climb rate and 22° turn rate per second.",
        repo: "",
        demo: "",
        details: [
          "Dash speed of Mach 2.2 with a 750 nautical mile combat radius",
          "Employs a low‑bypass afterburning turbofan for high thrust",
          "Exceptional climb rates (254 m/s) and short field performance",
          "Air‑to‑air refuelling enabled with a 22° turn rate per second"
        ],
        images: [
          "assets/project/sky_sentinel.png",
          "assets/project/weather_station.png"
        ]
      }
    ]
  },
  education: {
    items: [
      {
        degree: "Bachelor of Aerospace Engineering",
        school: "Aviation & Aerospace University, Bangladesh",
        gpa: "3.56 / 4.00",
        start: "Jan 2022",
        end: "Dec 2025",
        duration: "2022 – 2026",
        description:
          "Pursuing undergraduate studies focusing on aerospace engineering with emphasis on AI applications, predictive maintenance and unmanned aerial systems.",
        thesis: {
          title: "A Novel Integrated ML Framework with Comparative Evaluation for Fault Detection, Condition Monitoring, and RUL Prediction of Turbofan Engines",
          supervisor: "Dr. Saifur Rahman Bakaul",
          supervisorTitle: "Chairman, Department of Aerospace Engineering",
          status: "In Progress",
          objectives: [
            "Enhanced model accuracy through EDA, feature engineering, and SHAP-based interpretability",
            "Developed and deployed classical and neural network models via cloud-based API for aircraft engine health monitoring"
          ],
          technologies: [
            "Machine Learning",
            "Neural Networks",
            "Cloud API",
            "Python",
            "TensorFlow",
            "SHAP",
            "Aircraft Maintenance"
          ],
          keyAchievements: [
            "Comparative analysis of classical and deep learning models for predictive maintenance",
            "Cloud-based API deployment for real-time aircraft engine health monitoring",
            "SHAP-based model interpretability for domain experts",
            "Integration with aircraft maintenance systems"
          ]
        },
        glimpses: [
          "assets/project/logistics.png",
          "assets/project/sky_sentinel.png",
          "assets/project/voice_command.png"
        ]
      },
      {
        degree: "Higher Secondary Certificate (HSC), Science Division",
        school: "Sylhet Government College, Sylhet",
        gpa: "",
        start: "2017",
        end: "2019",
        duration: "2017 – 2019",
        description:
          "Completed the HSC with concentration in physics, mathematics, chemistry and biology.",
        glimpses: []
      },
      {
        degree: "Junior & Secondary School Certificate (JSC & SSC), Science",
        school: "Sylhet Government Pilot High School",
        gpa: "",
        start: "2014",
        end: "2017",
        duration: "2014 – 2017",
        description:
          "Built a solid foundation in science and mathematics during junior and secondary school.",
        glimpses: []
      }
    ],
    memories: []
  },
  publications: {
    items: [
      {
        title:
          "A Novel AI‑Driven Anomaly Detection Framework for Predictive Maintenance of Aircraft Engines",
        slug: "ai-anomaly-detection",
        authors: "Ishak, M. H.; Banarjee, J.; Oishy, A. S.; Bakaul, S. R.",
        venue: "IEEE, Scopus",
        year: "2025",
        status: "Published",
        image: "assets/publication/paper_meae_front_page_2025.png",
        keywords: ["predictive maintenance", "isolation forest", "machine learning", "anomaly detection", "aircraft engines"],
        abstract: "Predictive maintenance (PdM) utilizes data analysis and machine learning to monitor equipment condition by detecting anomalies...",
        link: "https://doi.org/10.1109/MEAE68077.2025.11557878",
        doi: "10.1109/MEAE68077.2025.11557878"
      },
      {
        title:
          "Static Structural Analysis And Material Selection of A Supersonic Fighter Aircraft's Wing",
        slug: "supersonic-wing-analysis",
        authors: "Ahmaad, M. U.; Sadi, A.; Banarjee, J.; Israq, A.; Bakaul, S. R.",
        venue: "AIAA Student Conference VII (3rd Place in Paper Team Category)",
        year: "2025",
        status: "Published",
        image: "assets/publication/AIAA_Paper_Structure_2025.png",
        keywords: ["structural analysis", "material selection", "supersonic aircraft", "FEA", "composite materials"],
        abstract: "An aircraft flying at supersonic speeds must structurally withstand the pressure forces encountered during flight...",
        link: "https://doi.org/10.2514/6.2025-106584",
        doi: "10.2514/6.2025-106584"
      },
      {
        title:
          "An AI-Driven Integrated Framework for Turbofan Engine Health Monitoring, Fault Diagnosis, and Prognostics",
        slug: "e2e-pdm-framework",
        authors: "Banarjee, J.; Ishak, M. H.; Bakaul, S. R.",
        venue: "Journal of Aerospace Information Systems, AIAA",
        year: "2026",
        status: "Under Review",
        image: "assets/publication/PdM_Journal_Paper.png",
        keywords: ["predictive maintenance", "deep learning", "condition monitoring", "prognostics", "aircraft engines"],
        abstract: "Predictive maintenance has emerged as a transformative strategy in aircraft engine health monitoring...",
        link: "",
        doi: ""
      },
      {
        title:
          "Sustainable Additive Manufacturing using rPET: Extrusion Design, Mechanical Testing, and Data-Driven Prediction",
        slug: "rpet-additive-manufacturing",
        authors: "Banarjee, J.; Himel, H. P.; Opul, T. W.; Anny, T.; Khan, S.",
        venue: "Rapid Prototyping Journal [Early draft: Second Place Tie, Paper Team Category, AIAA Student Conference VII, 2025]",
        year: "2026",
        status: "Under Review",
        image: "assets/publication/rPET_Paper_front_page.png",
        keywords: ["recycled PET", "additive manufacturing", "sustainability", "extrusion design", "data-driven prediction"],
        abstract: "This conference paper presents the development of an extrusion system for producing recycled PET filament...",
        link: "",
        doi: ""
      }
    ]
  },
  awards: {
    items: [
      {
        title: "NASA Space Apps Challenge Bangladesh – 1st Runner‑up",
        org: "NASA Space Apps BD",
        year: "2023",
        note: "Innovative solution for space/Earth science."
      },
      {
        title: "AAUB Scholarship",
        org: "AAUB",
        year: "—",
        note: "Awarded for academic excellence."
      }
    ]
  },
  memberships: {
    items: [
      { org: "AIAA", role: "Member", start: "—", end: "—" }
    ]
  },
  languages: {
    items: [
      { name: "Bangla", level: "Advanced" },
      { name: "English", level: "Advanced (writing)" }
    ]
  }
};


// Utility helpers for deep merging
function isObject(x) {
  return x !== null && typeof x === "object" && !Array.isArray(x);
}

export function deepMerge(target, source) {
  if (source === null || source === undefined) return target;
  if (Array.isArray(target) && Array.isArray(source)) return source;
  if (isObject(target) && isObject(source)) {
    const out = { ...target };
    for (const k of Object.keys(source)) {
      out[k] = k in target ? deepMerge(target[k], source[k]) : source[k];
    }
    return out;
  }
  return source;
}

// Data transformation helpers - UPDATED to preserve thesis and publication fields
export function transformLoadedData(raw) {
  const result = {};

  // profile
  if (raw.profile) {
    result.profile = { ...raw.profile };
    if (raw.profile.research_interests) {
      result.profile.research = raw.profile.research_interests;
    }
  }

  // skills
  if (raw.skills) {
    result.skills = raw.skills;
  }

  // experience
  if (raw.experience) {
    const exps = raw.experience.experiences || raw.experience.items || [];
    result.experience = {
      items: exps.map((exp) => {
        let start = "";
        let end = "";
        const dur = exp.duration || `${exp.start || ""} – ${exp.end || ""}`;
        if (dur) {
          const parts = dur.split("–").map((s) => s.trim());
          start = parts[0] || "";
          end = parts[1] || "";
        }
        let bullets = [];
        if (Array.isArray(exp.description)) bullets = exp.description;
        else if (Array.isArray(exp.bullets)) bullets = exp.bullets;
        return {
          org: exp.organization || exp.org || "",
          title: exp.role || exp.title || "",
          start,
          end,
          location: exp.location || "",
          bullets,
          logo: exp.logo || "",
          supervisor: exp.supervisor || "",
          images: exp.images || [],
          link: exp.link || "",
          source: exp.source || "",
          links: exp.links || []
        };
      })
    };
  }

  // projects
  if (raw.projects) {
    const projects = raw.projects.projects || raw.projects.items || [];
    result.projects = {
      items: projects.map((p) => {
        const tags = p.tags || (p.extra
          ? p.extra
            .split(/[,]/)
            .map((s) => s.trim())
            .filter(Boolean)
          : []);
        return {
          title: p.title || "",
          when: p.duration || p.when || "",
          summary: p.summary || "",
          tags,
          driveLink: p.drive_link || p.driveLink || "",
          repo: p.repo || "",
          demo: p.demo || "",
          image: p.image || "",
          extra: p.extra || "",
          newsLink: p.news_link || p.newsLink || "",
          details: p.details || [],
          images: p.images || []
        };
      })
    };
  }

  // education
  if (raw.education) {
    const edus = raw.education.education || raw.education.items || [];
    const memories = raw.education.memories || raw.education.moments || [];
    result.education = {
      items: edus.map((ed) => {
        let start = "";
        let end = "";
        const dur = ed.duration || `${ed.start || ""} – ${ed.end || ""}`;
        if (dur) {
          const parts = dur.split("–").map((s) => s.trim());
          start = parts[0] || "";
          end = parts[1] || "";
        }
        return {
          school: ed.school || "",
          degree: ed.degree || "",
          gpa: ed.gpa || "",
          start,
          end,
          duration: ed.duration || "",
          description: ed.description || "",
          glimpses: ed.glimpses || [],
          thesis: ed.thesis || undefined
        };
      }),
      memories
    };
  }

  // publications (FIXED: preserve slug, venue, year, and doi so ResearchPage displays full metadata)
  if (raw.publications) {
    const pubs = raw.publications.publications || raw.publications.items || [];
    result.publications = {
      items: pubs.map((p) => {
        const keywords = Array.isArray(p.keywords)
          ? p.keywords
          : p.keywords
            ? p.keywords
              .split(/[,;]/)
              .map((s) => s.trim())
              .filter(Boolean)
            : [];
        return {
          title: p.title || "",
          slug: p.slug || "",
          authors: p.authors || "",
          venue: p.venue || "",
          keywords,
          date: p.date || p.year || "",
          year: p.year || p.date || "",
          status: p.status || "",
          link: p.link || "",
          doi: p.doi || "",
          abstract: p.abstract || "",
          image: (p.image || "").replace(/\.PNG$/, ".png"),
          images: Array.isArray(p.images) ? p.images : []
        };
      })
    };
  }

  // awards
  if (raw.awards) {
    const awds = raw.awards.awards || raw.awards.items || [];
    result.awards = {
      items: awds.map((a) => ({
        title: a.title,
        result: a.result || "",
        org: a.organization || a.org || "",
        year: a.year || "",
        note: a.details || a.note || "",
        image: a.image || ""
      }))
    };
  }

  // certifications
  if (raw.certifications) {
    result.certifications = raw.certifications;
  }

  // memberships
  if (raw.memberships) {
    const mems = raw.memberships.memberships || raw.memberships.items || [];
    result.memberships = {
      items: mems.map((m) => ({
        org: m.organization || m.org || "",
        role: m.role || "",
        start: m.start || "",
        end: m.end || ""
      }))
    };
  }

  // languages
  if (raw.languages) {
    const langs = raw.languages.languages || raw.languages.items || [];
    result.languages = {
      items: langs.map((l) => ({
        name: l.name || "",
        level: l.level || ""
      }))
    };
  }

  // books
  if (raw.books) {
    const books = raw.books.books || raw.books.items || [];
    result.books = {
      items: books.map((b) => ({
        title: b.title || "",
        author: b.author || "",
        image: b.image || ""
      }))
    };
  }

  // movies
  if (raw.movies) {
    const movies = raw.movies.movies || raw.movies.items || [];
    result.movies = {
      items: movies.map((m) => ({
        title: m.title || "",
        year: m.year || "",
        image: m.image || ""
      }))
    };
  }

  // series
  if (raw.series) {
    const series = raw.series.series || raw.series.items || [];
    result.series = {
      items: series.map((s) => ({
        title: s.title || "",
        year: s.year || "",
        image: s.image || ""
      }))
    };
  }

  return result;
}

// Load external JSON data from the /data folder
export async function loadExternalData() {
  const mapping = {
    profile: "data/profile.json",
    skills: "data/skills.json",
    experience: "data/experience.json",
    projects: "data/projects.json",
    education: "data/education.json",
    publications: "data/publications.json",
    awards: "data/awards.json",
    memberships: "data/memberships.json",
    languages: "data/languages.json",
    certifications: "data/certifications.json",
    books: "data/books.json",
    movies: "data/movies.json",
    series: "data/series.json"
  };

  const loaded = {};
  for (const key of Object.keys(mapping)) {
    try {
      const res = await fetch(mapping[key]);
      if (res.ok) {
        loaded[key] = await res.json();
      }
    } catch (err) {
      console.error("Error loading", key, err);
    }
  }
  return transformLoadedData(loaded);
}
